import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';
import Pagination from '../components/Pagination';

export default function UserList() {
  useAuthGuard();

  const [sp, setSp] = useSearchParams();
  const initQ = sp.get('q') || '';

  const [keyword, setKeyword] = useState(initQ);
  const [queryKeyword, setQueryKeyword] = useState(initQ);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [cursorHistory, setCursorHistory] = useState([]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (queryKeyword) p.set('q', queryKeyword);
    setSp(p, { replace: true });
  }, [queryKeyword, setSp]);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const url = new URL(`${API_BASE_URL}/admin/users`);
        url.searchParams.set('limit', '20');
        if (cursor) url.searchParams.set('cursor', cursor);
        if (queryKeyword.trim()) url.searchParams.set('q', queryKeyword.trim());

        const res = await apiFetch(url.toString(), { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const list = data.data?.items || data.items || data.data || [];
        setItems(Array.isArray(list) ? list : []);
        setHasMore(data.data?.has_more ?? data.has_more ?? false);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Network error');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [cursor, queryKeyword]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCursor('');
    setCursorHistory([]);
    setQueryKeyword(keyword.trim());
  };

  const handleClear = () => {
    setKeyword('');
    setQueryKeyword('');
    setCursor('');
    setCursorHistory([]);
  };

  const handleNext = () => {
    if (items.length > 0) {
      const lastItem = items[items.length - 1];
      setCursorHistory((prev) => [...prev, cursor]);
      setCursor(lastItem.id || lastItem.user_id || '');
    }
  };

  const handlePrev = () => {
    setCursorHistory((prev) => {
      const next = [...prev];
      const prevCursor = next.pop();
      setCursor(prevCursor || '');
      return next;
    });
  };

  const fmtDate = (s) => {
    if (!s) return '-';
    const d = new Date(s);
    if (isNaN(d)) return s;
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  };

  return (
    <Layout>
      <div className="mb-4">
        <h2 className="mb-1">Users</h2>
        <p className="text-muted mb-0">Manage registered users</p>
      </div>

      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body">
          <form onSubmit={handleSearch} className="d-flex gap-2">
            <input
              type="search"
              className="form-control"
              placeholder="Search by email or name..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              style={{ maxWidth: '320px' }}
            />
            <button type="submit" className="btn btn-primary">Search</button>
            <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>
              Clear
            </button>
          </form>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Display Name</th>
                <th>Role</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="text-center py-4">Loading...</td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={5} className="text-danger py-4">{error}</td>
                </tr>
              )}
              {!loading && !error && items.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-4 text-muted">No users found</td>
                </tr>
              )}
              {!loading &&
                !error &&
                items.map((u) => (
                  <tr key={u.id || u.user_id}>
                    <td className="text-nowrap">
                      <code className="small">{(u.id || u.user_id || '').slice(0, 8)}...</code>
                    </td>
                    <td>{u.email || '-'}</td>
                    <td>{u.display_name || u.name || '-'}</td>
                    <td>
                      <span
                        className={`badge bg-${u.role === 'admin' ? 'danger' : 'secondary'}`}
                      >
                        {u.role || 'user'}
                      </span>
                    </td>
                    <td className="text-nowrap">{fmtDate(u.created_at)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="card-footer bg-white">
          <Pagination
            hasMore={hasMore}
            hasPrev={cursorHistory.length > 0}
            onNext={handleNext}
            onPrev={handlePrev}
            loading={loading}
          />
        </div>
      </div>
    </Layout>
  );
}
